import { useMemo } from 'react';
import { useAuthStore } from '@/store/auth.store';
import { highestRank, isAdmin } from '@/shared/api/types';

/**
 * Decode the `permission` claim array out of the current JWT. We don't
 * trust the JWT signature here (the server does that); we just read the
 * claims so the UI can decide which sidebar items / buttons to show.
 *
 * Returns an empty set if the token is missing, malformed, or carries no
 * permission claims (e.g. Phase-A roll-back). The downstream `.has()` then
 * fails closed — no surface shown until the next login refreshes the JWT.
 */
function readPermissionClaims(token: string | null): Set<string> {
  if (!token) return new Set();
  try {
    const payload = token.split('.')[1];
    if (!payload) return new Set();
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(
      decodeURIComponent(
        atob(padded)
          .split('')
          .map((c) => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
          .join(''),
      ),
    );
    const raw = json?.permission;
    if (!raw) return new Set();
    return new Set(Array.isArray(raw) ? raw : [raw]);
  } catch {
    return new Set();
  }
}

/**
 * Lightweight permission + role gates. Server still enforces — UI hiding
 * alone is never sufficient, but it keeps the surface honest.
 *
 * Permission API (preferred for new code):
 *   has(code)         → true if the JWT carries that permission claim
 *   hasAll(codes[])   → AND
 *   hasAny(codes[])   → OR
 *
 * Role API (legacy, kept for Phase-A backward-compat):
 *   isAdmin / isEditor / canManageUsers / canAssignRole / canManageUser
 *
 * As endpoints migrate to permission-based policies, callers should switch
 * to `.has('cms.posts.publish')` style checks. Existing role checks will
 * shim over to permission checks during Phase B cutover.
 */
export function usePermission() {
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const roles = user?.roles ?? [];
  const myRank = highestRank(roles);

  // Recompute only when the token actually rotates (login / refresh).
  const permissions = useMemo(() => readPermissionClaims(accessToken), [accessToken]);

  const has = (code: string) => permissions.has(code);
  const hasAll = (codes: string[]) => codes.every((c) => permissions.has(c));
  const hasAny = (codes: string[]) => codes.some((c) => permissions.has(c));

  return {
    // Permission API
    has,
    hasAll,
    hasAny,
    permissions,

    // Role API (Phase-A shims — still drive existing screens)
    isAdmin: isAdmin(roles),
    isEditor: roles.includes('Editor'),
    canManageUsers: isAdmin(roles),
    canAssignRole: (role: string) => myRank >= (role === 'Admin' ? 100 : 50),
    canManageUser: (targetRoles: string[]) => myRank >= highestRank(targetRoles),
    isSelf: (userId: string) => userId === user?.id,
    myRank,
    me: user,
  };
}
